import React from "react";
import { motion } from "framer-motion";
import {
  Phone,
  FileText,
  ShieldCheck,
  Clock3,
  BadgeCheck,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

const trustItems = [
  { icon: ShieldCheck, label: "Verified Staff" },
  { icon: BadgeCheck, label: "Background Checked" },
  { icon: Clock3, label: "Quick Response" },
];

const HireHero = () => {
  return (
    <section className="relative overflow-hidden min-h-[90vh] md:min-h-screen bg-[#fcfbff]">
      {/* Base background */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#ffffff_0%,#f7f3ff_42%,#fcfbff_100%)]" />

      {/* Soft luxury gradients */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_14%_18%,rgba(99,102,241,0.14),transparent_24%),radial-gradient(circle_at_84%_16%,rgba(168,85,247,0.10),transparent_22%),radial-gradient(circle_at_78%_78%,rgba(59,130,246,0.08),transparent_24%)]" />

      {/* Flowing ribbon art */}
      <div className="absolute inset-0 pointer-events-none">
        <svg
          className="absolute top-0 right-0 w-[1300px] max-w-none h-[760px] opacity-90"
          viewBox="0 0 1300 760"
          fill="none"
        >
          <path
            d="M1310 92C1148 22 961 18 807 92C635 175 528 340 342 391C218 425 108 409 0 359V0H1310V92Z"
            fill="url(#paint0_linear)"
            fillOpacity="0.75"
          />
          <path
            d="M1310 238C1166 174 1024 170 890 218C702 286 588 449 401 510C260 556 133 534 0 468V164C130 224 248 240 366 209C530 165 655 26 865 6C1021 -9 1163 30 1310 96V238Z"
            fill="url(#paint1_linear)"
            fillOpacity="0.58"
          />
          <path
            d="M1310 430C1190 386 1070 388 951 427C777 484 655 616 462 672C300 719 151 705 0 633V350C127 407 255 425 390 392C571 349 680 217 873 163C1028 119 1170 134 1310 190V430Z"
            fill="url(#paint2_linear)"
            fillOpacity="0.52"
          />
          <defs>
            <linearGradient id="paint0_linear" x1="1150" y1="40" x2="230" y2="430" gradientUnits="userSpaceOnUse">
              <stop stopColor="#C4B5FD" />
              <stop offset="0.5" stopColor="#E9D5FF" />
              <stop offset="1" stopColor="#DBEAFE" />
            </linearGradient>
            <linearGradient id="paint1_linear" x1="1230" y1="120" x2="180" y2="560" gradientUnits="userSpaceOnUse">
              <stop stopColor="#A5B4FC" />
              <stop offset="0.5" stopColor="#DDD6FE" />
              <stop offset="1" stopColor="#BFDBFE" />
            </linearGradient>
            <linearGradient id="paint2_linear" x1="1210" y1="220" x2="140" y2="700" gradientUnits="userSpaceOnUse">
              <stop stopColor="#C7D2FE" />
              <stop offset="0.5" stopColor="#F5D0FE" />
              <stop offset="1" stopColor="#E0F2FE" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Soft glows */}
      <div className="absolute -top-24 left-[-80px] w-[260px] h-[260px] rounded-full bg-indigo-200/25 blur-3xl" />
      <div className="absolute top-[18%] right-[-60px] w-[280px] h-[280px] rounded-full bg-purple-200/25 blur-3xl" />
      <div className="absolute bottom-[-100px] left-[8%] w-[220px] h-[220px] rounded-full bg-sky-200/20 blur-3xl" />

      {/* Texture */}
      <div className="absolute inset-0 opacity-[0.04] [background-image:radial-gradient(#0f172a_0.8px,transparent_0.8px)] [background-size:18px_18px]" />

      <div className="relative max-w-7xl mx-auto px-4 pt-28 pb-16 md:pt-36 md:pb-24 min-h-[90vh] md:min-h-screen flex items-center">
        <motion.div
          initial={{ opacity: 0, y: 34 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75 }}
          className="max-w-4xl"
        >
          <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white/80 backdrop-blur px-4 py-2 text-sm text-indigo-700 font-medium mb-6 shadow-sm">
            Trusted Home Help for Modern Families
          </span>

          <h1 className="text-[2.9rem] sm:text-5xl md:text-6xl lg:text-[4.5rem] font-bold leading-[0.95] tracking-[-0.03em] text-slate-900 mb-5">
            Help at Home,
            <br />
            <span className="text-slate-700">Without the Stress</span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-slate-700 max-w-2xl leading-relaxed mb-8">
            A calmer, safer way to hire trusted household support with guidance you can rely on from the start.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 max-w-3xl">
            <a
              href="https://wa.me/919665855163?text=Hi%20A2Z%20Consultancy,%20I%20would%20like%20to%20inquire%20about%20hiring%20maid,%20cook,%20driver,%20or%20caretaker%20services."
              target="_blank"
              rel="noreferrer"
              className="w-full sm:w-auto"
            >
              <button className="w-full sm:min-w-[220px] px-6 py-4 rounded-2xl bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-semibold text-base md:text-lg shadow-[0_16px_35px_rgba(18,140,126,0.22)] hover:scale-[1.02] transition flex items-center justify-center gap-2">
                <FaWhatsapp className="w-5 h-5" />
                WhatsApp
              </button>
            </a>

            <a href="tel:+919665855163" className="w-full sm:w-auto">
              <button className="w-full sm:min-w-[190px] px-6 py-4 rounded-2xl bg-white/85 backdrop-blur text-slate-900 border border-slate-200 font-semibold text-base md:text-lg shadow-md hover:scale-[1.02] transition flex items-center justify-center gap-2">
                <Phone className="w-5 h-5 text-indigo-600" />
                Call Now
              </button>
            </a>

            <a
              href="https://forms.gle/ZTXKbyfv1snHH2Hp6"
              target="_blank"
              rel="noreferrer"
              className="w-full sm:w-auto"
            >
              <button className="w-full sm:min-w-[190px] px-6 py-4 rounded-2xl bg-white/65 backdrop-blur text-slate-900 border border-indigo-100 font-semibold text-base md:text-lg shadow-md hover:scale-[1.02] transition flex items-center justify-center gap-2">
                <FileText className="w-5 h-5 text-violet-600" />
                Quick Form
              </button>
            </a>
          </div>

          <div className="flex flex-wrap gap-3">
            {trustItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/78 backdrop-blur border border-white/80 text-sm md:text-base text-slate-700 shadow-sm"
                >
                  <Icon className="w-4 h-4 text-indigo-600" />
                  {item.label}
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white/72 to-transparent" />
    </section>
  );
};

export default HireHero;